/**
 * Gameplay Test Scenarios
 * Integration tests for core game loops
 */

import { getTestRunner, expect, wait } from '../GameTestRunner';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';

/** Helper to get the current Colyseus room via ServiceRegistry */
function getRoom() {
  return ServiceRegistry.getInstance().get('NetworkManager')?.getRoom() ?? null;
}

/**
 * Register all gameplay test scenarios
 */
export function registerGameplayScenarios(): void {
  const runner = getTestRunner();

  // XP and Leveling Tests
  runner.addScenario({
    name: 'XP Gain and Level Up Flow',
    description: 'Tests XP gain, level up, and stat point award',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'XP Gain Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();

      // Get initial state
      const playerSchema = room.state.players.get(room.sessionId);
      if (!playerSchema) {
        return { name: 'XP Gain Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      const initialLevel = playerSchema.level;
      const initialXP = playerSchema.xp;
      const initialStatPoints = playerSchema.statPoints;

      // Add XP
      room.send('debugAddXP', { amount: 100 });

      // Wait for sync
      await wait(500);

      // Verify XP increased or level up occurred
      const currentXP = playerSchema.xp;
      const currentLevel = playerSchema.level;

      if (currentLevel > initialLevel) {
        // Level up occurred
        expect(currentLevel).toBeGreaterThan(initialLevel);
        expect(playerSchema.statPoints).toBeGreaterThan(initialStatPoints);
      } else {
        // XP increased
        expect(currentXP).toBeGreaterThan(initialXP);
      }

      return {
        name: 'XP Gain and Level Up Flow',
        passed: true,
        duration: performance.now() - startTime,
        details: { initialXP, currentXP, initialLevel, currentLevel }
      };
    }
  });

  // Stat Points Tests
  runner.addScenario({
    name: 'Stat Point Spending',
    description: 'Tests spending stat points on primary stats',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Stat Point Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Stat Point Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      // Ensure we have stat points
      if (playerSchema.statPoints <= 0) {
        // Level up to get points
        room.send('debugLevelUp', {});
        await wait(300);
      }

      const initialPoints = playerSchema.statPoints;
      const initialSTR = playerSchema.strength;

      // Spend a point on strength
      room.send('updateStats', { action: 'increase', stat: 'strength' });
      await wait(300);

      expect(playerSchema.statPoints).toBe(initialPoints - 1);
      expect(playerSchema.strength).toBe(initialSTR + 1);

      return {
        name: 'Stat Point Spending',
        passed: true,
        duration: performance.now() - startTime,
        details: { initialPoints, finalPoints: playerSchema.statPoints, initialSTR, finalSTR: playerSchema.strength }
      };
    }
  });

  // Combat Tests
  runner.addScenario({
    name: 'NPC Combat and XP Reward',
    description: 'Tests damaging NPC and receiving XP on kill',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Combat Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Combat Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      const initialXP = playerSchema.xp;

      // Find an NPC to damage
      let npc: any = null;
      room.state.npcs.forEach((n: any) => {
        if (!npc) npc = n;
      });

      if (!npc) {
        return { name: 'Combat Test', passed: false, duration: 0, error: 'No NPCs available' };
      }

      // Damage the NPC until dead
      const damageInterval = setInterval(() => {
        room.send('damageNPC', { npcId: npc.id, damage: 10 });
      }, 100);

      // Wait for kill
      await wait(1000);
      clearInterval(damageInterval);

      // Check if XP increased
      const currentXP = playerSchema.xp;

      return {
        name: 'NPC Combat and XP Reward',
        passed: currentXP > initialXP,
        duration: performance.now() - startTime,
        details: { initialXP, currentXP, npcKilled: !room.state.npcs.has(npc.id) }
      };
    }
  });

  // Chat System Tests
  runner.addScenario({
    name: 'Chat Message Send and Receive',
    description: 'Tests sending chat messages',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Chat Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();

      // Send a test message
      const testMessage = 'Test message ' + Date.now();
      room.send('chat', { message: testMessage });

      await wait(200);

      // Just verify no error occurred
      return {
        name: 'Chat Message Send and Receive',
        passed: true,
        duration: performance.now() - startTime,
        details: { message: testMessage }
      };
    }
  });

  // Teleport Tests
  runner.addScenario({
    name: 'Teleport Functionality',
    description: 'Tests teleporting to coordinates',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Teleport Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Teleport Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      const initialX = playerSchema.x;
      const initialY = playerSchema.y;

      // Teleport to a new location
      const targetX = 500;
      const targetY = 400;
      room.send('teleport', { x: targetX, y: targetY });

      await wait(300);

      expect(playerSchema.x).toBe(targetX);
      expect(playerSchema.y).toBe(targetY);

      return {
        name: 'Teleport Functionality',
        passed: true,
        duration: performance.now() - startTime,
        details: { from: { x: initialX, y: initialY }, to: { x: targetX, y: targetY } }
      };
    }
  });

  // Persistence Tests
  runner.addScenario({
    name: 'Character Save and Load',
    description: 'Tests that character data persists',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Persistence Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Persistence Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      // Modify stats
      const initialXP = playerSchema.xp;
      room.send('debugAddXP', { amount: 50 });
      await wait(300);

      const modifiedXP = playerSchema.xp;

      // Trigger save (if available)
      if (room.send) {
        room.send('save', {});
      }
      await wait(500);

      return {
        name: 'Character Save and Load',
        passed: modifiedXP > initialXP,
        duration: performance.now() - startTime,
        details: { initialXP, modifiedXP }
      };
    }
  });

  // Database Persistence Test
  runner.addScenario({
    name: 'Database Persistence Verification',
    description: 'Verifies stats are saved to and loaded from database',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'DB Persistence Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'DB Persistence Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      // Record initial state
      const initialStats = {
        xp: playerSchema.xp,
        level: playerSchema.level,
        strength: playerSchema.strength,
        dexterity: playerSchema.dexterity,
        intelligence: playerSchema.intelligence,
        luck: playerSchema.luck,
        statPoints: playerSchema.statPoints
      };

      // Modify all stats
      room.send('debugAddXP', { amount: 100 });
      await wait(200);

      // Request save
      room.send('savePlayer', {});
      await wait(500);

      // Verify stats were modified
      const modifiedStats = {
        xp: playerSchema.xp,
        level: playerSchema.level,
        strength: playerSchema.strength,
        dexterity: playerSchema.dexterity,
        intelligence: playerSchema.intelligence,
        luck: playerSchema.luck,
        statPoints: playerSchema.statPoints
      };

      const xpIncreased = modifiedStats.xp > initialStats.xp || modifiedStats.level > initialStats.level;

      return {
        name: 'Database Persistence Verification',
        passed: xpIncreased,
        duration: performance.now() - startTime,
        details: {
          initial: initialStats,
          modified: modifiedStats,
          xpIncreased,
          serverSaveTriggered: true
        }
      };
    }
  });

  // Stat Synchronization Test
  runner.addScenario({
    name: 'Stat Synchronization',
    description: 'Verifies all primary and derived stats sync correctly',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Stat Sync Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Stat Sync Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      // Check all expected stats exist
      const requiredStats = [
        'strength', 'dexterity', 'intelligence', 'luck',
        'attack', 'magicAttack', 'defense', 'magicDefense',
        'health', 'maxHealth', 'mana', 'maxMana',
        'xp', 'level', 'statPoints'
      ];

      const missingStats = requiredStats.filter(stat =>
        playerSchema[stat as keyof typeof playerSchema] === undefined
      );

      // Test sync by requesting force sync
      room.send('forceSyncStats', {});
      await wait(300);

      return {
        name: 'Stat Synchronization',
        passed: missingStats.length === 0,
        duration: performance.now() - startTime,
        details: {
          missingStats,
          allStatsPresent: missingStats.length === 0,
          syncRequested: true
        }
      };
    }
  });

  // Inventory Tests
  runner.addScenario({
    name: 'Inventory Item Usage',
    description: 'Tests using items from inventory',
    run: async () => {
      const room = getRoom();
      if (!room) {
        return { name: 'Inventory Test', passed: false, duration: 0, error: 'Not connected to server' };
      }

      const startTime = performance.now();
      const playerSchema = room.state.players.get(room.sessionId);

      if (!playerSchema) {
        return { name: 'Inventory Test', passed: false, duration: 0, error: 'Player schema not found' };
      }

      // Check if we have starter items
      if (playerSchema.inventory.size === 0) {
        return { name: 'Inventory Test', passed: false, duration: 0, error: 'No items in inventory' };
      }

      const initialHealth = playerSchema.health;
      const initialMana = playerSchema.mana;

      // Try to use first item
      const firstItem = Array.from(playerSchema.inventory.values())[0] as any;
      room.send('useItem', { itemId: firstItem?.id });

      await wait(300);

      return {
        name: 'Inventory Item Usage',
        passed: true,
        duration: performance.now() - startTime,
        details: {
          itemUsed: firstItem.name,
          initialHealth,
          currentHealth: playerSchema.health,
          initialMana,
          currentMana: playerSchema.mana
        }
      };
    }
  });

  gameLogger.info('TEST', 'Registered gameplay test scenarios');
}

export default registerGameplayScenarios;
